import { createFileRoute } from '@tanstack/react-router';
import { fetchOrderTags } from '@/api/orders';
import { SalesOrderOverviewTab } from '@/app/(client)/orders/-components/SalesOrderOverviewTab';
import {
  buyerCompaniesQueryOptions,
  useBuyerCompaniesDropdown,
} from '@/app/(client)/orders/-hooks/useBuyerCompaniesDropdown';
import {
  orderTypesQueryOptions,
  useOrderTypes,
} from '@/app/(client)/orders/-hooks/useOrderTypes';
import { useSalesOrderDetail } from '@/app/(client)/orders/-hooks/useSalesOrderDetail';
import type { SalesOrderDetail } from '@/app/(client)/orders/-types';
import {
  attachmentTemplatesQueryOptions,
  useAttachmentTemplatesForImport,
} from '@/hooks/useAttachmentTemplatesForImport';
import { useFacilityProcesses } from '@/hooks/useFacilityProcesses';
import { groupedFacilityProcessOptionsQueryOptions } from '@/hooks/useGroupedFacilityProcessOptions';
import { useOrderTags } from '@/hooks/useOrderTags';
import { queryClient } from '@/lib/utils/queryClient';

export const Route = createFileRoute(
  '/(client)/orders/(pages)/(detail)/sales-orders/$soId/overview',
)({
  loader: () =>
    Promise.all([
      queryClient.ensureQueryData({
        queryKey: ['cascade-tracing', 'order-tags'],
        queryFn: () => fetchOrderTags().then(r => r.data),
      }),
      queryClient.ensureQueryData(orderTypesQueryOptions),
      queryClient.ensureQueryData(buyerCompaniesQueryOptions),
      queryClient.ensureQueryData(attachmentTemplatesQueryOptions),
      queryClient.ensureQueryData(groupedFacilityProcessOptionsQueryOptions),
    ]),
  component: SalesOrderOverviewRoute,
});

function SalesOrderOverviewRoute() {
  const { soId } = Route.useParams();
  const { data: order } = useSalesOrderDetail(soId);
  const { data: orderTags } = useOrderTags();
  const { data: orderTypes } = useOrderTypes();
  const { data: buyerCompaniesData } = useBuyerCompaniesDropdown();
  const { data: templatesData } = useAttachmentTemplatesForImport();
  const { processes: fpProcesses } = useFacilityProcesses();

  if (!order) return null;

  // fetchSalesOrderById returns SalesOrder; the single-fetch endpoint also
  // returns the extra SalesOrderDetail fields (all optional) — safe to widen.
  return (
    <SalesOrderOverviewTab
      order={order as unknown as SalesOrderDetail}
      orderTags={orderTags ?? []}
      orderTypes={orderTypes ?? []}
      buyerCompanies={buyerCompaniesData?.data ?? []}
      facilityProcessOptions={fpProcesses ?? []}
      attachmentTemplates={templatesData?.data ?? []}
    />
  );
}
